import { extendTheme, defineStyleConfig, useColorModeValue } from "@chakra-ui/react";

const isPartner = window.location.hostname.includes('adec') || window.location.hostname.includes('a-dec');

const getSolidBgColor = (props) => {
  if (props.colorMode === "dark"){
    return "white";
  }
  return isPartner ? "adecBlueBackground" : "brand.300"
}

const StyledButton = defineStyleConfig({
  baseStyle: {
    fontWeight: "bold",
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0px 16px',
    gap: '10px',
    height: '44px',
  },
  variants: {
    solid: (props) => ({
      bg: getSolidBgColor(props),
      color: props.colorMode === "dark" ? "black" :  "lightButtonText",
      _hover: {
        bg: props.colorMode === "dark" ? "gray.200" : "brand.400",
        color: props.colorMode === "dark" ? "black" : "darkText",
      },
    }),
    passwordShow: {
      bg: "transparent",
      color: "inherit"
    },
    saveButton: (props) => ({
      bg: getSolidBgColor(props),
      color: props.colorMode === "dark" ? "black" : "lightButtonText",
      marginLeft: '10px'
    }),
    backButtonInbox: () => ({
      bg: "transparent",
      color: useColorModeValue("#154043", "#FFFFFF"),
    }),
    userInfoButton: () => ({
      justifyContent: "flex-start",
      gap: "2px",
      bg: "transparent",
      color: useColorModeValue("#154043", "#FFFFFF"),
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: "400",
      lineHeight: "140%",
    }),
    sendMessage: (props) => (
      {
        bg: getSolidBgColor(props),
        color: props.colorMode === "dark" ? "black" : "white",
        _hover: {
          bg: props.colorMode === "dark" ? "#E0E0E0" : "#123536",
        },
        _active: {
          bg: props.colorMode === "dark" ? "#C8C8C8" : "#0e2a2a",
        }
      }
    ),
    ellipsis: (props) => (
      {
        bg: props.colorMode === "dark" ? "#292c3a" : "#F9F9FB",
        color: props.colorMode === "dark" ? "white" : "black",
        _hover: {
          bg: props.colorMode === "dark" ? "#2d323f" : "gray.200",
        },
        _active: {
          bg: props.colorMode === "dark" ? "#2d323f" : "gray.300",
        }
      }
    )
  },
  defaultProps: {
    size: "md",
    variant: "solid",
  },
});

const ActionBar = defineStyleConfig({
  baseStyle: (props) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px",
    height: "50px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    backgroundColor: props.colorMode === "dark" ? "darkBackground" : "lightBackground",
    color: props.colorMode === "dark" ? "darkText" : "lightText",
    width: "100%",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
  }),
});

const ModalBox = defineStyleConfig({
  baseStyle: {
    borderRadius: '8px',
    padding: '16px',
    boxShadow: 'lg',
  },
  variants: {
    solid: (props) => ({
      bg: props.colorMode === "dark" ? "darkModalBackground" : "lightBackground",
      color: props.colorMode === "dark" ? "white" : "lightText",
    }),
  },
  defaultProps: {
    size: "md",
    variant: "solid",
  },
});

const AlertBox = defineStyleConfig({
  baseStyle: {
    borderRadius: '4px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    transition: 'background-color 0.3s ease, color 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '20px',
    padding: '4px 8px',
  },
  variants: {
    info: (props) => ({
      bg: props.colorMode === "dark" ? "gray.700" : "white",
      color: props.colorMode === "dark" ? "white" : "black",
    }),
  },
  defaultProps: {
    size: "md",
    variant: "info",
  },
});

const Divider = defineStyleConfig({
  baseStyle: (props) => ({
    borderColor: props.colorMode === "dark" ? "gray.700" : "gray.200",
  }),
});

const StyledSimpleGrid = defineStyleConfig({
  variants: {
    templateSelector: (props) => ({
      borderColor: props.selected ? "blue.500" : "transparent",
      borderWidth: props.selected ? "2px" : "1px",
      _hover: {
        bg: props.colorMode === "light" ? "gray.100" : "gray.700",
      },
      cursor: 'pointer'
    }),
  }
});

const theme = extendTheme({
  config: {
    initialColorMode: localStorage.getItem('chakra-ui-color-mode') || "light",
    useSystemColorMode: false,
  },
  styles: {
    global: (props) => ({
      "html, body": {
        padding: 0,
        margin: 0,
        width: "100%",
        height: "100%",
        bg: props.colorMode === "dark" ? "darkBackground" : "lightBackground",
        color: props.colorMode === "dark" ? "darkText" : "lightText",
      },
      ".chakra-box": {  // Apply styles to all divs and Chakra UI Box components
        backgroundColor: props.colorMode === "dark" ? "#212330" : "#F9F9FB", // Use Midnight for dark mode, Cloud White for light mode
        color: props.colorMode === "dark" ? "darkText" : "lightText",
      },
      ".chakra-flex": {  // Apply styles to all divs and Chakra UI Box components
        backgroundColor: props.colorMode === "dark" ? "#212330" : "#F9F9FB", // Use Midnight for dark mode, Cloud White for light mode
        color: props.colorMode === "dark" ? "darkText" : "lightText",
      },
      ".App": {
        padding: 0,
      },
      "*::before, *::after": {
        borderColor: "inherit",
      },
      ".chart-hover-effect:hover": {
        transform: "scale(1.05)",
        transition: "transform 0.2s ease-in-out",
      },
      ".alert-info": {
        marginBottom: "20px",
        gap: "8px",
        padding: "4px 8px",
        borderRadius: "16px",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        backgroundColor: props.colorMode === "dark" ? "gray.700" : "white",
      },
    }),
  },
  colors: {
    lightBackground: "#F9F9FB",
    darkBackground: "#11131B",
    lightText: isPartner ? "#003B5C" : "#092728",
    darkText: "#F9F9FB",
    lightButtonText: "#F9F9FB",
    darkModalBackground: "#212330",
    iconColor: isPartner? "#0072CE" : "",
    adecBlueBackground: "#0072CE",
    adecBlueText: "#003B5C",
    ghost: isPartner? "#0072CE" : "green",
    dividerColor: {
      light: isPartner? "#E5E5EF" : "gray.200",
      dark: "gray.700",
    },
    brand: {
      50: isPartner? "#f5f5f5" : "#e2f3f3", // Mint
      30: "#5BBBBD", // Verdigris
      20: "#2e8288", // Midnight Green
      10: "#092728", // Black
      100: isPartner? "#80b6da" : "#9fd7d8",
      200: "#2e8288", // verdigris
      300: isPartner? "#0072CE" : "#154043", // midnightGreen
      400: "#092728", // black
      500: "#e2f3f3", // mint - Main brand color (example)
      600: "#007ab8",
      700: "#006ba1",
      800: "#005885",
      900: "#003f5e",
    },
    secondary: {
      50: "#d2acd7", // Lightest shade
      100: "#b475bd",
      200: isPartner? "linear-gradient(#015A9D, #014674)" : "#8e4997",
      300: "#4e2853",
    },
    neutral: {
      50: "#e2f3f3", // Lightest shade
      100: "#9FA7BD", // neutralGray
      200: "#495A77", // darkGray
    },
    chartColors: {
      10: isPartner? "#007FE6" : "#4BA6B4",
      20: isPartner? "#2ADEFF" : "#F5EEC8",
      30: isPartner? "#003B5C" : "#BDD194",
      40: isPartner? "#22A9E7" : "#72C5A8",
      50: "#9C5490",
      60: "#6D2D63",
      70: "#4B1853"
    },
    lineChartColors: {
      10: "#8E4A97",
      30: "#5BBBBD"
    }
  },
  components: {
    Button: StyledButton,
    ActionBar,
    ModalBox,
    AlertBox,
    Divider,
    SimpleGrid: StyledSimpleGrid
  },
});

export default theme;
